import React, { useEffect, useState } from "react";
import {
  ModalHeader,
  ModalBody,
  ModalFooter,
  VStack,
  HStack,
  Text,
  Divider,
  Button,
  FormControl,
} from "@chakra-ui/react";
import { MdCancel, MdCheck } from "react-icons/md";
import { useDispatch } from "react-redux";
import { ALERT_STATUS_TYPE } from '@constants';
import DynamicFormField from '@components/DynamicFormField/DynamicFormField';
import { setAlert } from '@store/actions/alertActions';


const ActionButton = ({ onClick, isLoading, isDisabled, colorScheme, leftIcon, children }) => (
  <Button
    variant="outline"
    colorScheme={colorScheme}
    size="sm"
    border="2px solid"
    borderRadius="xl"
    isDisabled={isDisabled}
    isLoading={isLoading}
    onClick={onClick}
    leftIcon={leftIcon}
  >
    {children}
  </Button>
);


const ReviewProviderInputModal = ({
                                    title,
                                    fieldsGroup,
                                    onClose,
                                    onSubmit,
                                    isLoading
                                  }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({});

  useEffect(() => {
    const initial = {};
    Object.entries(fieldsGroup).forEach(([provider, fields]) => {
      initial[provider] = {};
      fields.forEach((field) => {
        initial[provider][field.name] = field.value ?? "";
      });
    });
    setFormData(initial);
  }, [fieldsGroup]);

  const handleInputChange = (provider, name, value) => {
    setFormData((prev) => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        [name]: value,
      },
    }));
  };

  const handleFormSubmit = async () => {
    let isValid = true;

    for (const [provider, fields] of Object.entries(fieldsGroup)) {
      for (const field of fields) {
        if (field.isRequired) {
          const value = formData[provider]?.[field.name];
          if (value === undefined || value === null || value === "") {
            isValid = false;
          }
        }
      }
    }

    if (!isValid) {
      dispatch(
        setAlert({
          title: "Please fill all required fields",
          status: ALERT_STATUS_TYPE.INFO,
        })
      );
      return;
    }

    // Prepare payload
    const providerResults = Object.entries(formData).map(([provider, fields]) => ({
      provider,
      ...fields,
    }));

    await onSubmit({ providerResults });
    onClose();
  };

  return (
    <>
      <ModalHeader>{title}</ModalHeader>
      <FormControl>
        <ModalBody>
          <VStack alignItems="stretch" spacing={6}>
            {Object.entries(fieldsGroup).map(([provider, fields]) => (
              <VStack
                key={provider}
                p={4} spacing={4}
                border={'1px dashed'}
                borderColor={'whiteAlpha.500'}
                borderRadius={'xl'}
                alignItems="stretch"
              >
                <Text fontSize="md" fontWeight="semibold">
                  Provider: {provider}
                </Text>
                <Divider />
                {fields.map((field) => (
                  <DynamicFormField
                    key={field.name}
                    fieldConfig={field}
                    data={formData[provider] ?? ""}
                    handleChange={(e) => {
                      const { name, value, type, checked } = e.target;
                      handleInputChange(
                        provider,
                        name,
                        type === "checkbox" ? checked : type === "number" ? Number(value) : value
                      );
                    }}
                  />
                ))}
              </VStack>
            ))}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack>
            <ActionButton
              onClick={onClose}
              isDisabled={isLoading}
              isLoading={false}
              colorScheme="red"
              leftIcon={<MdCancel />}
            >
              Cancel
            </ActionButton>
            <ActionButton
              onClick={handleFormSubmit}
              isLoading={isLoading}
              isDisabled={isLoading}
              colorScheme="brand"
              leftIcon={<MdCheck />}
            >
              Submit
            </ActionButton>
          </HStack>
        </ModalFooter>
      </FormControl>
    </>
  );
};

export default ReviewProviderInputModal;